import { v4 as uuidv4 } from 'uuid';
import { LIVE_CHAT_SDK_TOKEN_KEY, AccountType } from '@/utils/constants';
import { bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { getConfig, isBank, UseLiveChatSDKApp } from '@/config/core';
import { PartnerEntryPoint } from '@/config/constants';
import coreStore from '@/stores/core';
import { formatFromInUrl, getQueryForm } from '@/utils/instantLoaded/toolsWithoutDeps';
import { ENTRY_POINT } from '@/types/entryPoints';
import reporter from './report';
import { getAuthData, initTocWeb } from './auth';

type TokenKey = 'chatAccessToken' | 'tokenSuffix' | 'chatAccessTokenRemainTime' | 'chatbotUserId';
type SDKTokens = {
  [key in TokenKey]?: string;
};
type InitLiveChatOptions = {
  clearLastChatAccessToken?: boolean;
};

function updateSDKToken(key: TokenKey, value: string) {
  const originToken = getSDKToken();

  originToken[key] = value;

  try {
    window.localStorage.setItem(LIVE_CHAT_SDK_TOKEN_KEY, JSON.stringify(originToken));
  } catch (e) { }
}

function setAllSDKToken(tokens: SDKTokens) {
  Object.entries(tokens).forEach(([key, value]) => {
    updateSDKToken(key as TokenKey, value || '');
  });
}

// 存储在 storage 中的 token 可能会丢失，另外 chatAccessToken 获取失败的话也无法获取，所以在这里新增一个获取 token 的方式，
// 这里会增加 token 的重新获取逻辑，建议以后都使用此方法，但由于 async 的传染性，需要大规模更改调用方，
// 所以先在暴露问题的地方做变更，下面的原有 getSDKToken 方法建议不要再使用
export async function safeGetSDKToken() {
  const tokens = getSDKToken();
  const isTokenValid
    = Object.keys(tokens).length === 0 || !tokens.chatAccessToken || tokens.chatAccessToken.length === 0;
  if (isTokenValid) {
    await initLiveChatSDK();
    return getSDKToken();
  }

  return tokens;
}

export function getSDKToken() {
  const tokenInLocalStorage = window.localStorage.getItem(LIVE_CHAT_SDK_TOKEN_KEY) || '{}';
  let tokens: SDKTokens = {};

  try {
    tokens = tokens ? JSON.parse(tokenInLocalStorage) : {};
  } catch (e) {
    tokens = {};
  }

  if (isBank() && !tokens.chatAccessToken) {
    const authData = getAuthData();
    tokens.chatAccessToken = authData?.accessToken
  }

  return tokens;
}

export function getTokenSuffix() {
  let { tokenSuffix } = getSDKToken();

  if (!tokenSuffix) {
    tokenSuffix = uuidv4();
    updateSDKToken('tokenSuffix', tokenSuffix);
  }

  return tokenSuffix;
}

export let isBankNonLogin = false;

/** @type {boolean} canRegisterOncedCallViewWillReappear - 是否允许注册viewWillReappear事件，竞态情况下，liveChatGetToken callback快于viewWillReappear */
let canRegisterOncedCallViewWillReappear = true;

let isLiveChatGetTokenBridgeWork = false;

/** @type {boolean} oncedCallViewWillReappear - 只允许注册一次 */
let oncedCallViewWillReappear = false;

/** @type {boolean} oncedCallViewWillReappear - 事件callback */
let handelViewWillReappearCallback: ((...args: any[]) => any) | null;
let isGettingChatAccessToken = false; // 有正在获取token的
let rs: any;
let promise: Promise<any>;
async function getChatAccessToken(opts?: InitLiveChatOptions) {
  if (isBankNonLogin) {
    return;
  }

  if (isGettingChatAccessToken) {
    return await promise;
  }

  isGettingChatAccessToken = true;
  promise = new Promise<string | Error>((resolve) => {
    rs = resolve;
  });

  // 是否需要清除旧的 chatAccessToken
  if (opts?.clearLastChatAccessToken) {
    const tokens = getSDKToken();
    tokens.chatAccessToken = '';
    setAllSDKToken(tokens);
  }

  const { from } = coreStore.safeGetStore('initStore');

  const testToken = getQueryForm('chatAccessToken', window.location.search || window.CsChat?.searchUrl || '');

  if (testToken) {
    rs({ access_token: testToken }); // TODO: 去掉
  }

  bridgeCallFunc(() => {
    const { fromInUrl } = getConfig();
    const getTokenByBridge = () => {
      console.log('TODO:', 'getChatAccessToken, start call bridge liveChatGetToken');
      window.bridgeCallHandler(
        'liveChatGetToken',
        {
          sdk_param: JSON.stringify({
            entrypoint: formatFromInUrl(from) as ENTRY_POINT,
            accountType: AccountType[fromInUrl as keyof typeof PartnerEntryPoint],
          }),
        },
        function (res: { error: number; response: any }) {
          console.log('TODO:', 'getChatAccessToken, res=', { error: res?.error });
          if (isBank()) {
            isLiveChatGetTokenBridgeWork = true;
          }
          function logAndReportError(msg: string) {
            console.log(msg, res);
            let resString;
            try {
              resString = JSON.stringify(res);
            } catch {
              resString = '';
            }

            reporter.apiLog(msg, { level: 'error', res: resString });
          }

          if (res?.error === 0) {
            const { code, data } = res?.response || {};

            if (code === 0) {
              rs(data);
            } else {
              rs(new Error('code is not equal to 0'));
              logAndReportError('get chat token, chatbot error');
            }
          } else if (res?.error === -2 && isBank()) {
            // 未登录
            isBankNonLogin = true;
            (async function () {
              if (isBank() && isBankNonLogin) {
                try {
                  await initTocWeb();
                  rs();
                } catch (e) {
                  rs(new Error('code is not equal to 0'));
                }
              }
            })();
          } else {
            rs(new Error('http error'));
            logAndReportError('get chat token, bridge error');
          }

          isGettingChatAccessToken = false;
          if (isBank() && handelViewWillReappearCallback) {
            console.log('TODO:', 'getChatAccessToken, liveChatGetToken callback bridgeUnRegisterHandler viewWillReappear');
            window.bridgeUnRegisterHandler('viewWillReappear', handelViewWillReappearCallback);
            // handelViewWillReappearCallback = null;
            canRegisterOncedCallViewWillReappear = false;
          }
        },
      );
    }

    // 为了处理bank android bridge 内部mIsActive=false的标记造成所有bridge不能执行callback
    // 只绑定一次
    if (isBank() && canRegisterOncedCallViewWillReappear && !oncedCallViewWillReappear && window.isAndroid()) {
      oncedCallViewWillReappear = true;
      handelViewWillReappearCallback = () => {
        console.log('TODO:', 'getChatAccessToken, bridgeUnRegisterHandler viewWillReappear');
        window.bridgeUnRegisterHandler('viewWillReappear', handelViewWillReappearCallback);
        // handelViewWillReappearCallback = null;
        console.log('TODO:', 'viewWillReappear call getTokenByBridge');
        if (!isLiveChatGetTokenBridgeWork) {
          getTokenByBridge();
        }
      }
      if (handelViewWillReappearCallback) {
        console.log('TODO:', 'getChatAccessToken, bridgeRegisterHandler viewWillReappear');
        window.bridgeRegisterHandler('viewWillReappear', handelViewWillReappearCallback)
      }
    }

    getTokenByBridge();
  });

  return promise;
}

export async function initLiveChatSDK(opts?: InitLiveChatOptions) {
  const tokens = await getChatAccessToken(opts);
  const tokenSuffix = getTokenSuffix();

  if (tokens instanceof Error) {
    return new Error('get chat access token error');
  }

  setAllSDKToken({
    chatAccessToken: tokens?.access_token,
    chatbotUserId: tokens?.chatbot_user_id,
    tokenSuffix,
  });
}

// 见 safeGetSDKToken 注释描述
export async function safeGetLiveChatSDKHttpHeaders() {
  const tokens = await safeGetSDKToken();

  if (ENVIRONMENT !== 'live') {
    // 方便在 Web 端测试，直接使用 URL 参数中的 token
    const chatAccessToken = getQueryForm('chatAccessToken', window.location.search);
    return {
      'chat-access-token': chatAccessToken.length === 0 ? tokens.chatAccessToken || '' : chatAccessToken,
      'token-suffix': tokens.tokenSuffix || '',
    };
  }

  return {
    'chat-access-token': tokens.chatAccessToken || '',
    'token-suffix': tokens.tokenSuffix || '',
  };
}

export function getLiveChatSDKHttpHeaders() {
  const tokens = getSDKToken();

  if (ENVIRONMENT !== 'live') {
    // 方便在 Web 端测试，直接使用 URL 参数中的 token
    const chatAccessToken = getQueryForm('chatAccessToken', window.location.search);
    return {
      'chat-access-token': chatAccessToken.length === 0 ? tokens.chatAccessToken || '' : chatAccessToken,
      'token-suffix': tokens.tokenSuffix || '',
    };
  }

  return {
    'chat-access-token': tokens.chatAccessToken || '',
    'token-suffix': tokens.tokenSuffix || '',
  };
}

export function getLiveChatSDKBridgeParams() {
  if (!UseLiveChatSDKApp.is()) {
    return {};
  }

  const tokens = getSDKToken();
  const config = getConfig();

  return {
    chat_access_token: tokens.chatAccessToken || '',
    token_suffix: tokens.tokenSuffix || '',
    chat_access_token_remain_time: tokens.chatAccessTokenRemainTime ? parseInt(tokens.chatAccessTokenRemainTime) : 0,
    ...(config.csatParams || {}),
  };
}

export function setTokenRemainTime(remainTime: number) {
  updateSDKToken('chatAccessTokenRemainTime', `${remainTime}`);
}
